import { Dispatch } from 'redux';
import { toast } from 'react-toastify';
import Errors from '../../../shared/validation/errors';
import headersBuilder, { Method, buildPublicGetRequest } from '../../../utils/request';
import { mapLanguageCodesToLabels } from '../../../const/langs.const';
import LocalStorageUtils from '../../../utils/local-storage';

export const FETCH_SETTINGS_LANGUAGES = 'FETCH_SETTINGS_LANGUAGES';
export const SET_CONFIG_LANGUAGES = 'SET_CONFIG_LANGUAGES';
export const SET_CONFIG_DEFAULT_LANGUAGE = 'SET_CONFIG_DEFAULT_LANGUAGE';

export type FetchSettingsLanguagesAction = {
  type: string;
  settings: string[];
};

export type SetConfigLanguagesAction = {
  type: string;
  languages: Record<string, string>;
};

export type SetConfigDefaultLanguageAction = {
  type: string;
  defaultLanguage: string;
};

const normalizeLanguagesSettings = (settings: unknown): string[] => {
  const list = Array.isArray(settings) ? settings : (settings as any)?.Languages || [];

  return list
    .map((language: unknown) =>
      String(language || '')
        .trim()
        .toLowerCase()
    )
    .filter((language: string) => /^[a-z]{2}$/.test(language));
};

const fetchSettingsLanguages = (settings: string[]): FetchSettingsLanguagesAction => {
  return {
    type: FETCH_SETTINGS_LANGUAGES,
    settings
  };
};

const setConfigLanguages = (languages: Record<string, string>): SetConfigLanguagesAction => {
  return {
    type: SET_CONFIG_LANGUAGES,
    languages
  };
};

const setConfigDefaultLanguage = (defaultLanguage: string): SetConfigDefaultLanguageAction => {
  return {
    type: SET_CONFIG_DEFAULT_LANGUAGE,
    defaultLanguage
  };
};

const fetchLanguagesSettingsFromApi = (api: string): Promise<Response> => {
  return fetch(`${api}/settings/languages`, buildPublicGetRequest());
};

const postLanguagesSettingsToApi = (api: string, request: string[]): Promise<Response> => {
  return fetch(`${api}/settings/languages`, headersBuilder(Method.POST, request));
};

const postDefaultLanguageToApi = (api: string, request: { DefaultLanguage: string }): Promise<Response> => {
  return fetch(`${api}/settings/common`, headersBuilder(Method.POST, request));
};

export const fetchLanguagesSettingsAsync = (api: string) => {
  return (dispatch: Dispatch): void => {
    if (!api) {
      return;
    }

    fetchLanguagesSettingsFromApi(api)
      .then((response: Response) => {
        if (!response.ok) {
          return null;
        }
        return response.json();
      })
      .then((settings) => {
        if (!settings) {
          return;
        }

        const normalizedSettings = normalizeLanguagesSettings(settings);
        dispatch(fetchSettingsLanguages(normalizedSettings));
        dispatch(setConfigLanguages(mapLanguageCodesToLabels(normalizedSettings)));
      })
      .catch(() => {
        // Ignore errors: keep current frontend values.
      });
  };
};

export const editLanguagesSettingsAsync = (api: string, request: string[]) => {
  return (dispatch: Dispatch): void => {
    const normalizedRequest = normalizeLanguagesSettings(request);

    postLanguagesSettingsToApi(api, normalizedRequest)
      .then((response: Response) => Errors.checkResponseForErrors(response, api))
      .then((settings: any) => {
        const normalizedSettings = normalizeLanguagesSettings(settings?.length ? settings : normalizedRequest);
        dispatch(fetchSettingsLanguages(normalizedSettings));
        dispatch(setConfigLanguages(mapLanguageCodesToLabels(normalizedSettings)));
        toast.success('Languages settings edited successfully!', {
          position: toast.POSITION.BOTTOM_RIGHT
        });
      })
      .catch((error: Error) => Errors.throwErrorMessage(error));
  };
};

export const editLanguagesWithDefaultLanguageSettingsAsync = (api: string, request: string[], defaultLanguage: string) => {
  return (dispatch: Dispatch): void => {
    const normalizedRequest = normalizeLanguagesSettings(request);
    const normalizedDefaultLanguage = String(defaultLanguage || '')
      .trim()
      .toLowerCase();

    postLanguagesSettingsToApi(api, normalizedRequest)
      .then((response: Response) => Errors.checkResponseForErrors(response, api))
      .then((settings: any) => {
        const normalizedSettings = normalizeLanguagesSettings(settings?.length ? settings : normalizedRequest);
        dispatch(fetchSettingsLanguages(normalizedSettings));
        dispatch(setConfigLanguages(mapLanguageCodesToLabels(normalizedSettings)));

        return postDefaultLanguageToApi(api, { DefaultLanguage: normalizedDefaultLanguage });
      })
      .then((response: Response) => Errors.checkResponseForErrors(response, api))
      .then((settings: any) => {
        const responseDefaultLanguage = String(settings?.DefaultLanguage || normalizedDefaultLanguage)
          .trim()
          .toLowerCase();
        LocalStorageUtils.putValueToLocalStorage('lang', responseDefaultLanguage);
        dispatch(setConfigDefaultLanguage(responseDefaultLanguage));
        toast.success('Languages settings edited successfully!', {
          position: toast.POSITION.BOTTOM_RIGHT
        });
      })
      .catch((error: Error) => Errors.throwErrorMessage(error));
  };
};
